import { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Container, Stack, Typography, Button, CircularProgress, Backdrop } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getEmployees, moveEmployees } from '../utils/requests/employeesRequest';

/**
 * @typedef EmployeePosition
 * @property {number|undefined} vertical
 * @property {number|undefined} horizontal
 */

/**
 * @typedef Employee
 * @property {number} id
 * @property {string} name
 * @property {string} role
 * @property {string} photo
 * @property {EmployeePosition} position
 */

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  margin: `0 ${grid}px 0 0`,
  padding: isDragging ? 2 : 0,
  ...draggableStyle
});

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'lightblue' : 'lightgrey',
  display: 'flex',
  padding: grid,
  overflow: 'auto'
});

export default function MoveEmployee() {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * @type {Employee[][]}
   */
  const initEmployee = [[]];
  const [employees, setEmployees] = useState(initEmployee);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    getEmployees()
      .then((employees) => {
        /**
         * @type {{[key: number]: Employee[]}}
         */
        const groups = employees.reduce(
          (h, obj) =>
            Object.assign(h, {
              [obj.position.vertical]: (h[obj.position.vertical] || []).concat(obj)
            }),
          {}
        );
        const result = Object.keys(groups).map((key) => {
          /**
           * @type {Employee[]}
           */
          const result = groups[key];
          return result;
        });
        result.push([]);
        setEmployees(result);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const getList = (id) => employees[getKeyFromDraggable(id)];

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      const items = reorder(getList(source.droppableId), source.index, destination.index);
      const newEmployees = [...employees];
      newEmployees[getKeyFromDraggable(destination.droppableId)] = items;
      setEmployees(newEmployees);
    } else {
      const result = move(
        getList(source.droppableId),
        getList(destination.droppableId),
        source,
        destination
      );

      let newEmployees = [...employees];
      Object.keys(result).forEach((key) => {
        newEmployees[getKeyFromDraggable(key)] = result[key];
        newEmployees = newEmployees.filter((employees) => employees.length);
        newEmployees.push([]);
        setEmployees(newEmployees);
      });
    }
  };

  const submit = () => {
    /**
     * @type {{[id: number]: string}}
     *
     */
    const positions = {};
    for (let y = 0; y < employees.length; y += 1) {
      for (let x = 0; x < employees[y].length; x += 1) {
        const employee = employees[y][x];
        positions[employee.id] = `${y}-${x}`;
      }
    }

    setIsLoading(true);

    moveEmployees(positions).then((x) => {
      setIsLoading(false);
      toast.success(x.data.message);
    });
  };

  const getDraggableGroupKey = (key) => `droppable-${key}`;
  const getDraggableKey = (id) => `draggable-${id}`;
  const getKeyFromDraggable = (groupKey) => Number(groupKey.split('-')[1]);

  return (
    <Container>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Typography variant="h4" sx={{ mb: 5 }}>
        Employees Reorder
      </Typography>
      <Stack spacing={2}>
        <DragDropContext onDragEnd={(e) => onDragEnd(e)}>
          {employees &&
            employees.map((employee, key) => (
              <Droppable
                droppableId={getDraggableGroupKey(key)}
                key={getDraggableGroupKey(key)}
                direction="horizontal"
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                    {...provided.droppableProps}
                  >
                    {employee.map((item, index) => (
                      <Draggable
                        key={getDraggableKey(item.id)}
                        draggableId={getDraggableKey(item.id)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                          >
                            {item.content}
                            <img
                              src={item.photo}
                              alt={item.name}
                              style={{ width: 75, height: 75 }}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
        </DragDropContext>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            type="submit"
            onClick={() => submit()}
          >
            Save
          </LoadingButton>
          <Button disabled={isLoading} variant="text" onClick={() => navigate('../')}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
